import { BackButton } from "@/components/auth";
import type { ErrorItem } from "@auth0/auth0-acul-react/types";
import { useId, useRef, useState } from "react";
import { usePhoneIdentifierChallengeManager } from "../hooks/usePhoneIdentifierChallengeManager";

const CELLS = 6;

interface PhoneIdentifierChallengeFormProps {
  onBack?: () => void;
  identifier?: string;
}

export function PhoneIdentifierChallengeForm({
  onBack,
  identifier,
}: PhoneIdentifierChallengeFormProps) {
  const { handleSubmitPhoneChallenge, useErrors, texts, locales } =
    usePhoneIdentifierChallengeManager();

  const [digits, setDigits] = useState<string[]>(Array(CELLS).fill(""));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const uid = useId();
  const refs = useRef<(HTMLInputElement | null)[]>([]);

  const buttonText = texts?.buttonText || locales.form.buttonLabelText;
  const codeLabelText = texts?.placeholder || locales.form.field.labelText;
  const { errors, hasError } = useErrors;

  // Extract general errors (not field-specific) from the SDK
  const generalErrors: ErrorItem[] =
    errors.byType("auth0")?.filter((error) => {
      return !error.field || error.field === null;
    }) || [];

  const codeSDKError = errors.byField("code")[0]?.message;

  const code = digits.join("");
  const canSubmit = !isSubmitting && code.length === CELLS;

  const focusCell = (index: number) => {
    refs.current[Math.max(0, Math.min(CELLS - 1, index))]?.focus();
  };

  const updateDigit = (index: number, value: string) => {
    const next = [...digits];
    next[index] = value;
    setDigits(next);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const raw = e.target.value.replace(/\D/g, "");
    if (!raw) {
      updateDigit(index, "");
      return;
    }
    const digit = raw.slice(-1); // take last digit typed
    updateDigit(index, digit);
    e.target.dataset.filled = "true";
    if (index < CELLS - 1) focusCell(index + 1);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.key === "Backspace") {
      if (digits[index]) {
        updateDigit(index, "");
        const el = refs.current[index];
        if (el) el.dataset.filled = "";
      } else {
        focusCell(index - 1);
      }
    } else if (e.key === "ArrowLeft") {
      focusCell(index - 1);
    } else if (e.key === "ArrowRight") {
      focusCell(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, CELLS);
    const next = Array(CELLS).fill("");
    pasted.split("").forEach((ch, i) => {
      next[i] = ch;
    });
    setDigits(next);
    focusCell(Math.min(pasted.length, CELLS - 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      await handleSubmitPhoneChallenge(code);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="auth-view" onSubmit={handleSubmit} noValidate>
      {/* Identifier context line */}
      <div className="auth-code-prompt">
        {identifier ? (
          <>
            Enter the code sent to{" "}
            <span className="auth-code-target">{identifier}</span>.
          </>
        ) : (
          "Enter the code sent to your phone."
        )}
      </div>

      {/* Auth0 general error banner */}
      {hasError && generalErrors.length > 0 && (
        <div className="auth-error-banner" role="alert">
          {generalErrors.map((error) => (
            <div key={error.id}>{error.message}</div>
          ))}
        </div>
      )}

      <div className="auth-fields">
        {/* Phone Code 6-digit input field */}
        <label
          htmlFor={`${uid}-cell-0`}
          className="auth-label"
          style={{ display: "block", marginBottom: "8px" }}
        >
          {`${codeLabelText}*`}
        </label>

        <div
          className="auth-code-grid"
          role="group"
          aria-label="Verification code"
        >
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => {
                refs.current[i] = el;
              }}
              id={`${uid}-cell-${i}`}
              type="text"
              inputMode="numeric"
              autoComplete={i === 0 ? "one-time-code" : "off"}
              maxLength={1}
              className="auth-code-cell"
              data-filled={digit ? "true" : undefined}
              value={digit}
              aria-label={`Digit ${i + 1}`}
              onChange={(e) => handleChange(e, i)}
              onKeyDown={(e) => handleKeyDown(e, i)}
              onPaste={handlePaste}
              autoFocus={i === 0}
            />
          ))}
        </div>

        {/* Field specific error display */}
        {codeSDKError && (
          <div
            className="auth-error-banner"
            role="alert"
            style={{ marginTop: "12px" }}
          >
            {codeSDKError}
          </div>
        )}
      </div>

      <div className="auth-submit-block">
        <button type="submit" className="auth-btn" disabled={!canSubmit}>
          {isSubmitting ? locales.form.field.submitButtonText : buttonText}
        </button>

        {onBack && (
          <BackButton onClick={onBack}>Use a different phone number</BackButton>
        )}
      </div>
    </form>
  );
}

export default PhoneIdentifierChallengeForm;
